import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CalendarDays, MapPin, ArrowRight, ArrowUpRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

export default function Events() {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const upcoming = events.filter((e) => e.upcoming);
  const past = events.filter((e) => !e.upcoming);

  return (
    <main data-testid="events-page" className="bg-[#FAFAF8]">
      {/* BIG BANNER */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/34597472/pexels-photo-34597472.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1920"
            alt=""
            className="img-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-28 md:py-40 text-[#FAFAF8]">
          <div className="kicker text-[#F4A930] mb-6">Appy Events</div>
          <h1 className="font-sora text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.04] max-w-4xl">
            Gatherings that feel personal.
          </h1>
          <p className="mt-6 max-w-2xl text-[#FAFAF8]/85 text-lg md:text-xl leading-relaxed">
            Founder dinners, yoga Sundays, chef's tables and private socials — hosted at our managed properties, designed to be intimate.
          </p>
          <div className="mt-10 flex gap-3 flex-wrap">
            <a href="#upcoming" className="inline-flex items-center gap-2 rounded-full bg-[#F4A930] hover:bg-[#d99825] text-[#0E2E24] font-sora font-semibold px-6 py-3.5 transition-colors">
              See upcoming <ArrowRight size={15} />
            </a>
            <Link to="/partner" className="inline-flex items-center gap-2 rounded-full border border-[#FAFAF8]/30 bg-transparent hover:bg-[#FAFAF8]/10 text-[#FAFAF8] font-sora font-medium px-6 py-3.5 transition-colors">
              Host an event with us
            </Link>
          </div>
        </div>
      </section>

      <section id="upcoming" className="max-w-7xl mx-auto px-6 lg:px-12 py-20 md:py-24">
        <Tabs defaultValue="upcoming" data-testid="events-tabs">
          <TabsList className="bg-white border border-[#0E2E24]/10 rounded-full p-1">
            <TabsTrigger value="upcoming" className="rounded-full data-[state=active]:bg-[#0E2E24] data-[state=active]:text-[#FAFAF8] px-6 py-2 font-sora text-sm font-medium" data-testid="events-tab-upcoming">Upcoming ({upcoming.length})</TabsTrigger>
            <TabsTrigger value="past" className="rounded-full data-[state=active]:bg-[#0E2E24] data-[state=active]:text-[#FAFAF8] px-6 py-2 font-sora text-sm font-medium" data-testid="events-tab-past">Past ({past.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="mt-10">
            <EventGrid items={upcoming} />
          </TabsContent>
          <TabsContent value="past" className="mt-10">
            <EventGrid items={past} />
          </TabsContent>
        </Tabs>
      </section>
    </main>
  );
}

function EventGrid({ items }) {
  if (!items.length)
    return <div className="py-16 text-center text-[#5B6B63] font-sora">No events here yet. Check back soon.</div>;

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((e) => (
        <article
          key={e.id}
          className="group bg-white border border-[#0E2E24]/10 rounded-2xl overflow-hidden hover-lift"
          data-testid={`events-page-card-${e.id}`}
        >
          <div className="relative aspect-[5/4] overflow-hidden">
            <img src={e.image} alt={e.title} className="img-cover zoom-img" loading="lazy" />
            <div className="absolute top-4 left-4 px-3 py-1 bg-[#FAFAF8]/95 rounded-full text-[10px] tracking-widest uppercase font-sora font-semibold text-[#0E2E24]">
              {e.category}
            </div>
          </div>
          <div className="p-6">
            <div className="flex flex-wrap gap-4 text-xs font-sora text-[#5B6B63] mb-3">
              <span className="flex items-center gap-1"><CalendarDays size={12} /> {new Date(e.date).toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" })}</span>
              <span className="flex items-center gap-1"><MapPin size={12} /> {e.location.split(",")[0]}</span>
            </div>
            <h3 className="font-sora text-xl font-semibold text-[#0E2E24] leading-tight">{e.title}</h3>
            <p className="mt-3 text-sm text-[#5B6B63] leading-relaxed line-clamp-3">{e.description}</p>
            <Button
              variant="ghost"
              className="mt-5 -ml-3 text-[#1F5A43] hover:bg-[#E8F2EC] font-sora text-sm font-medium gap-1.5"
              data-testid={`event-cta-${e.id}`}
            >
              Request invite <ArrowUpRight size={14} />
            </Button>
          </div>
        </article>
      ))}
    </div>
  );
}
